import { useAppSelector } from '@/hooks/hooks';
import { envConfig } from '@/lib/env-config';
import { supabase } from '@/lib/supabase';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom hook to manage Supabase realtime subscription for section recording
 *
 * This hook:
 * 1. Starts subscription when ANY section recording begins
 * 2. Creates subscription only once per appointment session
 * 3. Clears subscription when appointment is deselected (back button pressed)
 *
 * @param enabled - Whether to enable the subscription
 */
export function useSectionRecordingSubscription(enabled: boolean = true) {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(
    null
  );
  const hasInitializedRef = useRef(false);

  // Get scribe job ID and section recording state from Redux
  const scribeJobId = useAppSelector(
    (state) => state.sectionRecording.scribeJobId
  );
  const activeRecording = useAppSelector(
    (state) => state.sectionRecording.activeRecording
  );
  const appointmentId = useAppSelector(
    (state) => state.sectionRecording.appointmentId
  );

  /**
   * Initialize subscription when:
   * - Subscription is enabled
   * - We have a scribeJobId
   * - Subscription hasn't been created yet
   */
  useEffect(() => {
    // Don't proceed if disabled, no scribeJobId, or already initialized
    if (!enabled || !scribeJobId || hasInitializedRef.current) {
      return;
    }

    // Check if there's an active section recording to trigger initialization
    if (!activeRecording?.sectionName) {
      return;
    }

    console.log(
      '🔌 [Section Recording Subscription] Initializing for scribeJobId:',
      scribeJobId
    );

    const schema = envConfig.supabase.schema || 'eyevantage';
    const channelName = `ev_scribe_sections_${scribeJobId}`;

    // Create the channel
    const channel = supabase.channel(channelName);

    // Subscribe to both INSERT and UPDATE events on ev_scribe_sections
    channel
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema,
          table: 'ev_scribe_sections',
          filter: `scribe_job_id=eq.${scribeJobId}`,
        },
        (payload) => {
          console.log(
            '📥 [Section Recording Subscription] New section inserted:',
            payload
          );
          // Handle INSERT event - new section created by AI
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema,
          table: 'ev_scribe_sections',
          filter: `scribe_job_id=eq.${scribeJobId}`,
        },
        (payload) => {
          console.log(
            '📥 [Section Recording Subscription] Section updated:',
            payload
          );
          // Handle UPDATE event - section data updated by AI
        }
      )
      .subscribe((status, err) => {
        console.log(
          `📡 [Section Recording Subscription] Status changed: ${status}`
        );

        if (status === 'SUBSCRIBED') {
          console.log(
            '✅ [Section Recording Subscription] Successfully subscribed to sections'
          );
          setIsConnected(true);
          setError(null);
          hasInitializedRef.current = true;
        } else if (status === 'CHANNEL_ERROR') {
          console.error(
            '❌ [Section Recording Subscription] Channel error:',
            err
          );
          setIsConnected(false);
          setError(err?.message || 'Subscription error');
        } else if (status === 'CLOSED') {
          console.log('🔒 [Section Recording Subscription] Channel closed');
          setIsConnected(false);
        }
      });

    subscriptionRef.current = channel;

    console.log(
      '🎉 [Section Recording Subscription] Setup complete for scribeJobId:',
      scribeJobId
    );

    // No cleanup here - we want the subscription to persist
    // Cleanup only happens when appointment changes or component unmounts
  }, [enabled, scribeJobId, activeRecording?.sectionName]);

  /**
   * Cleanup subscription when:
   * - Component unmounts
   * - Appointment changes (scribeJobId or appointmentId changes)
   */
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        console.log(
          '🧹 [Section Recording Subscription] Cleaning up subscription'
        );
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
        setIsConnected(false);
        hasInitializedRef.current = false;
      }
    };
  }, [scribeJobId, appointmentId]);

  /**
   * Manual cleanup function that can be called from parent component
   * This is useful when the back button is pressed
   */
  const cleanup = useCallback(() => {
    if (subscriptionRef.current) {
      console.log(
        '🧹 [Section Recording Subscription] Manual cleanup triggered'
      );
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
      setIsConnected(false);
      setError(null);
      hasInitializedRef.current = false;
    }
  }, []);

  return {
    isConnected,
    error,
    cleanup,
  };
}
